"use client";

import { useState } from "react";
import { Search, Plus, Users } from "lucide-react";
import { PractitionersPanel, Practitioner } from "../../../components/practitioners/PractitionersPanel";
import { PractitionerModal, PractitionerFormData } from "../../../components/practitioners/PractitionerModal";
import PageHeader from "../../../components/ui/PageHeader";

// Demo data
const DEMO_PRACTITIONERS: Practitioner[] = [
  {
    id: "1",
    name: "Dr. Sarah Johnson",
    email: "sarah.johnson@clinic.com",
    phone: "+44 7700 100001",
    specialty: "Aesthetic Medicine",
    status: "active",
  },
  {
    id: "2",
    name: "Dr. Michael Chen",
    email: "michael.chen@clinic.com",
    phone: "+44 7700 100002",
    specialty: "Dermatology",
    status: "active",
  },
  {
    id: "3",
    name: "Emma Williams",
    email: "emma.williams@clinic.com",
    phone: "+44 7700 100003",
    specialty: "Laser Treatments",
    status: "active",
  },
  {
    id: "4",
    name: "Dr. James Wilson",
    email: "james.wilson@clinic.com",
    phone: "+44 7700 100004",
    specialty: "Cosmetic Surgery",
    status: "inactive",
  },
  {
    id: "5",
    name: "Sophie Brown",
    email: "sophie.brown@clinic.com",
    phone: "+44 7700 100005",
    specialty: "Skin Care",
    status: "active",
  },
];

export default function ManagePractitioner() {
  const [searchTerm, setSearchTerm] = useState("");
  const [practitioners, setPractitioners] = useState<Practitioner[]>(DEMO_PRACTITIONERS);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [editingPractitioner, setEditingPractitioner] = useState<Practitioner | null>(null);

  // Filter practitioners by search
  const filteredPractitioners = practitioners.filter((p) =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase().trim()) ||
    p.email.toLowerCase().includes(searchTerm.toLowerCase().trim()) ||
    p.specialty.toLowerCase().includes(searchTerm.toLowerCase().trim())
  );

  // Handlers
  const handleAddPractitioner = () => {
    setModalMode("add");
    setEditingPractitioner(null);
    setShowModal(true);
  };

  const handleEditPractitioner = (practitioner: Practitioner) => {
    setModalMode("edit");
    setEditingPractitioner(practitioner);
    setShowModal(true);
  };

  const handleDeletePractitioner = (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      setPractitioners((prev) => prev.filter((p) => p.id !== id));
    }
  };

  const handleSavePractitioner = (data: PractitionerFormData) => {
    if (modalMode === "add") {
      const newPractitioner: Practitioner = {
        id: Date.now().toString(),
        name: data.name,
        email: data.email,
        phone: data.phone,
        specialty: data.specialty,
        status: data.status,
      };
      setPractitioners((prev) => [...prev, newPractitioner]);
    } else if (editingPractitioner) {
      setPractitioners((prev) =>
        prev.map((p) =>
          p.id === editingPractitioner.id
            ? { ...p, ...data }
            : p
        )
      );
    }
    setShowModal(false);
  };

  return (
    <div className="p-4 lg:p-6">
      <div className="flex flex-col w-full h-full">

        {/* Header */}
        <PageHeader
          title="Practitioners"
          description="Manage your aesthetic practitioners and staff"
          icon={Users}
          variant="card"
          className="mb-0"
          mobileActionsInHeader={true}
          actions={
            <button
              onClick={handleAddPractitioner}
              className="px-3 py-1.5 lg:px-6 lg:py-3 bg-[#885ABB] hover:bg-[#6f47a2] text-white rounded-lg text-[10px] lg:text-base font-bold lg:font-medium transition-all transform hover:-translate-y-0.5 flex items-center gap-1.5 lg:gap-2 shadow-sm"
            >
              <Plus className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
              <span>Add Practitioner</span>
            </button>
          }
        />

        {/* Toolbar */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between py-4 gap-4">
          {/* Left: Count */}
          <div className="text-sm text-gray-500 font-medium">
            {filteredPractitioners.length} practitioner{filteredPractitioners.length !== 1 ? "s" : ""}
          </div>

          {/* Right: Search */}
          <div className="relative w-full lg:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search practitioners..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-[14px] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-transparent transition-all"
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 pb-6">
          <PractitionersPanel
            practitioners={filteredPractitioners}
            onPractitionerEdit={handleEditPractitioner}
            onPractitionerDelete={handleDeletePractitioner}
          />
        </div>

        {/* Modal */}
        <PractitionerModal
          isOpen={showModal}
          mode={modalMode}
          practitioner={editingPractitioner}
          onClose={() => setShowModal(false)}
          onSave={handleSavePractitioner}
        />
      </div>
    </div>
  );
}
